import React, { useState, useCallback } from 'react';
import { IProductLine2, ProductLineClient, SetProductLineProductRequest } from '../../../services/WebApiClient';
import { replaceInArray } from '../../../util/replaceInArray';
import { useClient } from '../../../util/useClient';
import { usePromise } from '../../EditorCommon/usePromise';
import { LoadSpinner } from '../../LoadSpinner';
import { SortBy } from '../../Table/SortBy';
import { IColumn, Table } from '../../Table/Table';


interface IPlp {
    productId: number;
    externalId: string;
    name: string;
    isActive: boolean;
    isChanged: boolean;
}

export function ProductLineProductAdmin(props: {
    onChange: () => void;
    productLine: Readonly<IProductLine2>
}) {

    const client = useClient(ProductLineClient);
    const [productLineProducts, setProductLineProducts] = useState<IPlp[] | undefined>();
    const [updateCount, setUpdateCount] = useState(0);

    usePromise(useCallback(async () => {
        const uc = updateCount;
        const response = await client.getProductLineProducts(props.productLine.id);
        setProductLineProducts(response.productLineProducts.map(plp => ({
            isActive: plp.isActive,
            isChanged: false,
            productId: plp.productId,
            externalId: plp.externalId,
            name: plp.name,
        })));
        return {
            productLineProducts: response.productLineProducts,
            uc
        };
    },
        [client, updateCount, props.productLine.id]
    ));

    function setAllActive(active: boolean) {
        const newPlps = productLineProducts?.map(plp => {
            const isChanged = plp.isChanged || plp.isActive !== active;
            return { ...plp, isActive: active, isChanged };
        });
        setProductLineProducts(newPlps);
    }

    function replacePlp(productId: number, replacement: Partial<IPlp>) {
        if (!productLineProducts)
            return;
        const newProductLineProducts = replaceInArray(productLineProducts,
            p => p.productId === productId,
            p => ({ ...p, ...replacement, isChanged: true })
        ) ?? productLineProducts;
        setProductLineProducts(newProductLineProducts);
    }

    async function save() {
        if (!productLineProducts)
            return;
        setProductLineProducts(undefined);
        try {
            const requests = productLineProducts
                .filter(plp => plp.isChanged)
                .map(plp => client.setProductLineProduct(
                    props.productLine.id,
                    plp.productId,
                    new SetProductLineProductRequest({
                        isActive: plp.isActive
                    })
                ));
            await Promise.allSettled(requests);
        }
        finally {
            setUpdateCount(updateCount + 1);
            props.onChange();
        }
    }

    if (!productLineProducts)
        return <LoadSpinner />
    const allActive = productLineProducts.every(plp => plp.isActive);

    const columns: IColumn<IPlp>[] = [
        {
            header: (<>
                <input type="checkbox"
                    checked={allActive}
                    onChange={evt => setAllActive(evt.target.checked)}
                />
                <span>Active</span>
            </>),
            content: plp => (<input type="checkbox"
                checked={plp.isActive}
                onChange={evt => replacePlp(plp.productId, { isActive: evt.target.checked })}
            />),
            key: "isActive",
            sort: SortBy.Number(plp => plp.isActive ? 1 : 0)
        },
        {
            header: "External ID",
            content: plp => plp.externalId,
            key: "externalId",
            sort: SortBy.String(plp => plp.externalId)
        },
        {
            header: "Name",
            content: plp => plp.name,
            key: "name",
            sort: SortBy.String(plp => plp.name)
        },
    ];


    return (
        <>
            <Table columns={columns}
                rows={productLineProducts}
                rowKey={plp => plp.productId}
            />
            <button className="btn btn-primary"
                onClick={save}>
                Save
            </button>

        </>
    );
}
